/**
 * Centered confirmation modal (replaces window.confirm for better visibility).
 * @module components/common/ConfirmModal
 */

import React, { useEffect } from 'react';
import { Button } from '@sequelae/lore-core';
import './ConfirmModal.css';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional: 'danger' for destructive actions (red confirm button) */
  variant?: 'default' | 'danger';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
}) => {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="confirm-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
      onClick={onCancel}
    >
      <div
        className="confirm-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-modal-title" className="confirm-modal-title">
          {title}
        </h2>
        <p id="confirm-modal-message" className="confirm-modal-message">
          {message}
        </p>
        <div className="confirm-modal-actions">
          <Button
            type="button"
            variant="secondary"
            size="medium"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <span className={variant === 'danger' ? 'confirm-modal-button-danger-wrap' : undefined}>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
};
